import React from "react";
import { Text } from "@imminently/react/ui/core/text";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { RadioGroup, RadioGroupProps } from "./components";
import {
  Observation,
  ObservationNeuroMovementArm,
  ObservationNeuroMovementLeg,
  armMovementLabels,
  legMovementLabels,
} from "@/resources/types";
import type { UseFormSetValue, UseFormWatch } from "react-hook-form";


const getArmMovementValue = (v: string): ObservationNeuroMovementArm => {
  switch (v) {
    case "normal": return "normal";
    case "extension": return "extension";
    case "mild": return "mild";
    case "severe": return "severe";
    case "spastic": return "spastic";
    default: return "none";
  }
};
const getLegMovementValue = (v: string): ObservationNeuroMovementLeg => {
  switch (v) {
    case "normal": return "normal";
    case "extension": return "extension";
    case "mild": return "mild";
    case "severe": return "severe";
    default: return "none";
  }
};

const SegmentRow = styled("div")`
  display: flex;
  gap: 2rem;

  >* {
    width: calc(50% - 1rem);
  }
`;
const SegmentHeading = styled(Text)`
  text-align: center;
`;

type SegmentProps = {
  prefix: string;
  options: RadioGroupProps["options"];
  leftValue: RadioGroupProps["value"];
  setLeftValue: RadioGroupProps["setValue"];
  rightValue: RadioGroupProps["value"];
  setRightValue: RadioGroupProps["setValue"];
}
const Segment: React.FC<SegmentProps> = p => {
  const { prefix, options, leftValue, rightValue, setLeftValue, setRightValue, } = p;

  return (
    <Stack gap={2}>
      <SegmentHeading variant='h5'>{prefix} Movement</SegmentHeading>

      <SegmentRow>
        <SegmentHeading variant='h6'>Left</SegmentHeading>
        <SegmentHeading variant='h6'>Right</SegmentHeading>
      </SegmentRow>

      <SegmentRow>
        <RadioGroup
          options={options}
          value={leftValue}
          setValue={setLeftValue}
        />

        <RadioGroup
          options={options}
          value={rightValue}
          setValue={setRightValue}
        />
      </SegmentRow>
    </Stack>
  );
};


const armOptions: Array<{ label: string; value: ObservationNeuroMovementArm }> = [
  { label: armMovementLabels.normal, value: "normal" },
  { label: armMovementLabels.mild, value: "mild" },
  { label: armMovementLabels.severe, value: "severe" },
  { label: armMovementLabels.spastic, value: "spastic" },
  { label: armMovementLabels.extension, value: "extension" },
  { label: armMovementLabels.none, value: "none" },
];
const legOptions: Array<{ label: string; value: ObservationNeuroMovementLeg }> = [
  { label: legMovementLabels.normal, value: "normal" },
  { label: legMovementLabels.mild, value: "mild" },
  { label: legMovementLabels.severe, value: "severe" },
  { label: legMovementLabels.extension, value: "extension" },
  { label: legMovementLabels.none, value: "none" },
];

export type LimbMovementProps = {
  watch: UseFormWatch<Pick<Observation, "neuro">>
  setValue: UseFormSetValue<Pick<Observation, "neuro">>
}
export const LimbMovement: React.FC<LimbMovementProps> = p => {
  const { watch, setValue } = p;

  return (
    <Stack gap={4}>
      <Segment
        prefix="Arm"
        options={armOptions}
        leftValue={watch("neuro.movement.armLeft")}
        setLeftValue={v => setValue("neuro.movement.armLeft", getArmMovementValue(v))}
        rightValue={watch("neuro.movement.armRight")}
        setRightValue={v => setValue("neuro.movement.armRight", getArmMovementValue(v))}
      />

      <Segment
        prefix="Leg"
        options={legOptions}
        leftValue={watch("neuro.movement.legLeft")}
        setLeftValue={v => setValue("neuro.movement.legLeft", getLegMovementValue(v))}
        rightValue={watch("neuro.movement.legRight")}
        setRightValue={v => setValue("neuro.movement.legRight", getLegMovementValue(v))}
      />
    </Stack>
  );
};
